import React from 'react';
import {
  IconButton,
  Select,
  Slider,
  Button,
  MenuItem,
  ListItemText,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import './PlaybackBar.scss';

const PlaybackSpeeds = [
  { label: '1x', value: 1 },
  { label: '2x', value: 2 },
  { label: '5x', value: 5 },
  { label: '10x', value: 10 },
  { label: '20x', value: 20 },
  { label: '25x', value: 25 },
  { label: '50x', value: 50 },
  { label: '100x', value: 100 },
];

function PlaybackBar({
  playbackSpeeds,
  playbackSpeed,
  setPlaybackSpeed,
  playPlayback,
  pausePlayback,
  playbackPosition,
  setPlaybackPosition,
  minPlaybackPosition,
  maxPlaybackPosition,
  disabled,
}) {
  const dateFormat = 'MM/DD/YYYY';
  const timeFormat = 'HH:mm:ss.SSS';

  let maxPlaybackPositionTime = maxPlaybackPosition;
  let minPlaybackPositionTime = minPlaybackPosition;
  const playbackIncrement = 15 * 60 * 1000;
  if (
    maxPlaybackPositionTime - minPlaybackPositionTime
    < playbackIncrement * 3
  ) {
    maxPlaybackPositionTime += playbackIncrement;
    minPlaybackPositionTime -= playbackIncrement;
  }
  const maxPlaybackPositionFormatted = moment(maxPlaybackPositionTime).format(
    `${dateFormat} ${timeFormat}`,
  );
  const minPlaybackPositionFormatted = moment(minPlaybackPositionTime).format(
    `${dateFormat} ${timeFormat}`,
  );
  return (
    <div className="playback-bar">
      <div className="main-controls">
        <IconButton
          aria-label="play"
          onClick={playPlayback}
          disabled={disabled}
        >
          <PlayArrowIcon />
        </IconButton>
        <IconButton
          aria-label="pause"
          onClick={pausePlayback}
          disabled={disabled}
        >
          <PauseIcon />
        </IconButton>
      </div>
      <div className="slider-container">
        <Slider
          className="slider"
          min={minPlaybackPositionTime}
          max={maxPlaybackPositionTime}
          value={playbackPosition}
        />
        <div className="slider-controls">
          <span className="start-time">{minPlaybackPositionFormatted}</span>
          <div className="controls">
            <Button
              disabled={disabled}
              startIcon={<KeyboardDoubleArrowLeftIcon />}
              onClick={() => setPlaybackPosition(-1 * playbackIncrement)}
              aria-label="forward-15"
            >
              15 min
            </Button>
            <Select
              className="playback-select"
              disabled={disabled}
              value={playbackSpeed}
              onChange={(evt) => setPlaybackSpeed(evt.target?.value)}
              variant="outlined"
            >
              {playbackSpeeds.map((speed) => (
                <MenuItem
                  key={speed.label}
                  value={speed.value}
                  onClick={setPlaybackSpeed}
                >
                  <ListItemText primary={speed.label} />
                </MenuItem>
              ))}
            </Select>
            <Button
              disabled={disabled}
              endIcon={<KeyboardDoubleArrowRightIcon />}
              onClick={() => setPlaybackPosition(playbackIncrement)}
              aria-label="backwards-15"
            >
              15 min
            </Button>
          </div>
          <span className="end-time">{maxPlaybackPositionFormatted}</span>
        </div>
      </div>
    </div>
  );
}

PlaybackBar.propTypes = {
  playbackSpeeds: PropTypes.arrayOf(PropTypes.object).isRequired,
  playbackSpeed: PropTypes.number.isRequired,
  setPlaybackSpeed: PropTypes.func.isRequired,
  playPlayback: PropTypes.func.isRequired,
  pausePlayback: PropTypes.func.isRequired,
  playbackPosition: PropTypes.number,
  setPlaybackPosition: PropTypes.func.isRequired,
  minPlaybackPosition: PropTypes.number,
  maxPlaybackPosition: PropTypes.number,
  disabled: PropTypes.bool,
};

PlaybackBar.defaultProps = {
  playbackPosition: 0,
  minPlaybackPosition: 0,
  maxPlaybackPosition: 100,
  disabled: false,
};

export { PlaybackBar, PlaybackSpeeds };
